/**
 * Formato dei vocali e degli audio inviati con un numero "Ufficiale".
 *
 * Meta (documentazione Media, settembre 2026) accetta come audio:
 *   audio/aac, audio/amr, audio/mpeg, audio/mp4, audio/ogg (solo codec OPUS, mono).
 * audio/webm NON e' accettato. I messaggi vocali (con il tasto play) devono
 * essere Ogg/Opus e si mandano con `audio.voice = true`; sopra 512 KB WhatsApp
 * mostra un'icona di download invece del play.
 *
 * Il problema: Chrome registra WebM/Opus anche se il file viene chiamato
 * "voice.ogg" con tipo audio/ogg. Qui si guarda il CONTENUTO del file (primi
 * byte), non l'etichetta:
 *   - Ogg ("OggS")      -> inviato cosi' com'e', come vocale
 *   - WebM (EBML)       -> riconfezionato in Ogg/Opus senza ricodifica (webmOpusToOgg)
 *   - MP4/M4A, MP3, AAC, AMR -> inviati come audio normale con il tipo giusto
 *   - altro             -> errore chiaro, niente invio destinato a fallire
 *
 * planCloudAudio e' puro (testato); prepareCloudAudio legge il Blob.
 */

import { isWebm, webmOpusToOgg } from './audio/webmOpusToOgg';

export type AudioContainer = 'ogg' | 'webm' | 'mp4' | 'mp3' | 'aac' | 'amr' | 'unknown';

export function sniffAudioContainer(head: Uint8Array): AudioContainer {
  const at = (i: number) => head[i] ?? -1;
  const ascii = (start: number, s: string) => [...s].every((ch, i) => at(start + i) === ch.charCodeAt(0));
  if (ascii(0, 'OggS')) return 'ogg';
  if (isWebm(head)) return 'webm';
  if (ascii(4, 'ftyp')) return 'mp4';
  if (ascii(0, '#!AMR')) return 'amr';
  if (ascii(0, 'ID3')) return 'mp3';
  if (at(0) === 0xff && (at(1) & 0xf6) === 0xf0) return 'aac'; // ADTS
  if (at(0) === 0xff && (at(1) & 0xe0) === 0xe0) return 'mp3'; // frame MPEG audio
  return 'unknown';
}

const MIME: Record<Exclude<AudioContainer, 'webm' | 'unknown'>, { mime: string; ext: string }> = {
  ogg: { mime: 'audio/ogg', ext: 'ogg' },
  mp4: { mime: 'audio/mp4', ext: 'm4a' },
  mp3: { mime: 'audio/mpeg', ext: 'mp3' },
  aac: { mime: 'audio/aac', ext: 'aac' },
  amr: { mime: 'audio/amr', ext: 'amr' },
};

export interface CloudAudioPlan {
  bytes: Uint8Array;
  mime: string;
  filename: string;
  /** true = messaggio vocale (Ogg/Opus): si invia con audio.voice = true. */
  voice: boolean;
  /** Canali dichiarati dal file Ogg (Meta vuole mono): utile per un avviso. */
  channels?: number;
}

export class CloudAudioError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CloudAudioError';
  }
}

/** Decide come mandare i byte di un audio. Lancia CloudAudioError se non si puo'. */
export function planCloudAudio(bytes: Uint8Array): CloudAudioPlan {
  const kind = sniffAudioContainer(bytes.subarray(0, 16));
  if (kind === 'webm') {
    // Input strano (file troncato, varianti WebM non previste): mai un'eccezione, solo il messaggio chiaro sotto.
    let ogg: ReturnType<typeof webmOpusToOgg> = null;
    try { ogg = webmOpusToOgg(bytes); } catch { ogg = null; }
    if (!ogg) {
      throw new CloudAudioError(
        'Questo vocale e\' in un formato che WhatsApp Ufficiale non accetta e non si e\' potuto convertire. Riprova la registrazione.',
      );
    }
    return { bytes: ogg.bytes, mime: 'audio/ogg', filename: 'vocale.ogg', voice: true, channels: ogg.channels };
  }
  if (kind === 'unknown') {
    throw new CloudAudioError('Formato audio non accettato da WhatsApp Ufficiale: usa OGG, MP3, M4A, AAC o AMR.');
  }
  const { mime, ext } = MIME[kind];
  return {
    bytes,
    mime,
    filename: kind === 'ogg' ? 'vocale.ogg' : `audio.${ext}`,
    voice: kind === 'ogg',
    channels: kind === 'ogg' ? oggChannels(bytes) : undefined,
  };
}

/** Canali letti dall'intestazione OpusHead della prima pagina Ogg. */
function oggChannels(bytes: Uint8Array): number | undefined {
  const segs = bytes[26];
  const start = 27 + (segs ?? 0);
  const isHead = [...'OpusHead'].every((ch, i) => bytes[start + i] === ch.charCodeAt(0));
  return isHead ? bytes[start + 9] : undefined;
}

/** Legge il Blob e restituisce il file pronto per l'upload a Meta. */
export async function prepareCloudAudio(audio: Blob): Promise<{ file: Blob; filename: string; voice: boolean; channels?: number }> {
  const bytes = new Uint8Array(await audio.arrayBuffer());
  const plan = planCloudAudio(bytes);
  // Copia in un ArrayBuffer proprio: Blob non accetta viste su buffer condivisi.
  const copy = new Uint8Array(plan.bytes.byteLength);
  copy.set(plan.bytes);
  return { file: new Blob([copy.buffer], { type: plan.mime }), filename: plan.filename, voice: plan.voice, channels: plan.channels };
}
